import React, { useEffect, useState } from "react";
import { Box } from "@mui/system";
import { useNavigate } from "react-router-dom";
import CustomModal from "../../../../components/Modals/CustomModal";
import { Button } from "../../../../components/Buttons";
import InputField from "../../../../components/InputFields/InputField";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import Checkbox from "../../../../components/Buttons/Checkbox/Checkbox";
import IconComponent from "../../../../components/Icon/IconComponent";
import { CircularLoader, HtmlEditor } from "../../../../components";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import {
  EMITTER_ERROR,
  EMITTER_SUCCESS,
} from "../../../../utils/constants";
import {
  authEndpoints,
  CampaignSettingsEndpoints,
} from "../../../../utils/constants/httpConstants";
import {
  getRequest,
  patchRequest,
  postRequest,
} from "../../../../Apis/apiWrapper";
import {
  addTagStyleInHtml,
  compressString,
  convertBase64,
  decompressString,
  getErrorMessage,
  removeTagStyleFromHtml,
} from "../../../../utils/helpers";
import {
  campaignValidation,
} from "../../../../utils/validations/inputValidations";
import { GREY, PRIMARY } from "../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../utils/constants/UI";
import { STRINGS } from "../../../Marketing/strings";
import { styles } from "./styles";

const SOURCE_GMAIL = "gmail";
const SOURCE_CUSTOM = "custom";

// FE-only sentinel used when a user saved a Gmail-mode signature without
// extracting / typing any HTML. The backend rejects an explicit
// `signature_mode` field, so we round-trip the chosen flow through the
// signature_html slot - when it equals this marker we treat the body as
// empty in the editor while still recognising the record as Gmail-mode.
const GMAIL_MODE_MARKER = '<div data-sig-mode="gmail"></div>';

const HTML_EDITOR_ID = "signature-html-editor";
const UNSUB_EDITOR_ID = "signature-unsub-editor";

const DEFAULT_UNSUB = `<div>If you'd rather not receive future emails from us, you may <span data-dt="dynamicTag" data-id="{{unsubscribe_tag}}" style="background: rgb(255, 233, 233); color: rgb(255, 72, 72); width: fit-content; border-radius: 4px; font-size: small; font-family: inherit; padding: 4px 8px;">{{unsubscribe_tag}}</span></div>`;

const FIELD_LABELS = {
  zip_code: "Postal/Zip",
  phone_number: "Phone number",
  signature_name: "Signature name",
  first_name: "Sender's first name",
  last_name: "Sender's last name",
  job_title: "Job title",
  company_name: "Company name",
  address_line_1: "Address line 1",
  address_line_2: "Address line 2",
  city: "City",
  state: "State",
  country: "Country",
};

const emptyDraft = {
  signature_name: "",
  first_name: "",
  last_name: "",
  job_title: "",
  company_name: "",
  phone_number: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  country: "",
  zip_code: "",
  is_default: false,
  unsubscribe_message: "",
  signature_html: "",
  image_url: "",
  image_name: "",
};

const MAX_IMAGE_BYTES = 1024 * 1024; // 1 MB

// Letters, spaces, hyphens, apostrophes, periods, commas, ampersands.
// Used for fields that should never contain digits (names, city/state/country).
const ALPHA_FIELD_REGEX = /^[A-Za-zÀ-ɏ\s\-'.,&]+$/;

const validateAlphaField = (value, friendlyLabel, { required = true } = {}) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    if (required) {
      return { isValid: false, message: `${friendlyLabel} is required.` };
    }
    return { isValid: true, message: "" };
  }
  if (!ALPHA_FIELD_REGEX.test(trimmed)) {
    return {
      isValid: false,
      message: `${friendlyLabel} can only contain letters.`,
    };
  }
  return { isValid: true, message: "" };
};

const validatePhoneNumber = (value, { required = false } = {}) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    if (required) {
      return { isValid: false, message: "Phone number is required." };
    }
    return { isValid: true, message: "" };
  }
  // Strip the formatting characters we let through in onChange.
  const digits = trimmed.replace(/[^\d]/g, "");
  if (digits.length < 7) {
    return {
      isValid: false,
      message: "Phone number must be at least 7 digits.",
    };
  }
  if (digits.length > 15) {
    return {
      isValid: false,
      message: "Phone number must be 15 digits or fewer.",
    };
  }
  return { isValid: true, message: "" };
};

// Both /pull-gmail-signatures and the legacy single-signature record store the
// HTML compressed + URI-encoded; new records may store it raw.
// A tag-less value is NOT proof of compression - a signature typed straight
// into the editor with no formatting is stored as bare text. decompressString
// returns null for those, and decodeURIComponent(null) yields the *string*
// "null" (it doesn't throw), which used to get rendered into the editor.
const decodeStoredSignatureHtml = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  if (/<[^>]+>/.test(raw)) return raw;
  const decompressed = decompressString(raw);
  if (decompressed == null) return raw;
  try {
    return decodeURIComponent(decompressed);
  } catch (e) {
    return decompressed;
  }
};

const SignatureFormModal = ({
  open,
  onClose,
  onSaved,
  accountId,
  accountEmail,
  accountProvider,
  signatureId,
  isFirstForMailbox = false,
}) => {
  const classes = styles();
  const navigate = useNavigate();
  const isEdit = Boolean(signatureId);

  const [draft, setDraft] = useState(emptyDraft);
  const [validations, setValidations] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  // Edit mode: pre-lock to a sentinel so the source picker never flashes while
  // /signature/get/:id is in flight; the inferred mode replaces it after fetch.
  // Create mode: null until the user picks.
  const [source, setSource] = useState(isEdit ? "loading" : null);
  const [showUnsub, setShowUnsub] = useState(false);
  // The HtmlEditor is contentEditable - React state isn't kept in sync as the
  // user types, so we listen to native input events to toggle the placeholder.
  const [editorIsEmpty, setEditorIsEmpty] = useState(true);

  // Reset every time the modal opens or the target signature changes.
  useEffect(() => {
    if (!open) return;
    if (!isEdit) {
      setDraft({
        ...emptyDraft,
        is_default: isFirstForMailbox,
        unsubscribe_message: DEFAULT_UNSUB,
      });
      setValidations({});
      setSource(null);
      setShowUnsub(true);
      return;
    }
    // Lock the picker out while we load the existing signature.
    setSource("loading");
    (async () => {
      try {
        setIsFetching(true);
        const res = await getRequest(
          navigate,
          `${CampaignSettingsEndpoints.signatureGetById}/${signatureId}`,
        );
        const sig = res?.data?.data || {};
        const s = sig.signature || {};
        const rawStoredHtml = decodeStoredSignatureHtml(
          sig.signature_html || s.signature_html || "",
        );
        // Detect (and strip) the FE-only Gmail-mode marker so the editor opens
        // empty rather than showing a stray invisible div.
        const isGmailMarker =
          rawStoredHtml.replace(/\s+/g, "") ===
          GMAIL_MODE_MARKER.replace(/\s+/g, "");
        const storedHtml = isGmailMarker ? "" : rawStoredHtml;
        const hasStructured = Boolean(
          s.job_title ||
            s.company_name ||
            s.phone_number ||
            s.address_line_1 ||
            s.city ||
            s.state ||
            s.country ||
            s.zip_code,
        );
        // Persisted source-of-truth wins (Gmail-mode signatures with empty body
        // or custom-mode signatures with empty fields would otherwise
        // misclassify on edit). Fall back to inference for legacy records.
        const persistedMode =
          sig.signature_mode === SOURCE_GMAIL ||
          sig.signature_mode === SOURCE_CUSTOM
            ? sig.signature_mode
            : null;
        setDraft({
          signature_name: sig.signature_name || "",
          first_name: s.first_name || "",
          last_name: s.last_name || "",
          job_title: s.job_title || "",
          company_name: s.company_name || "",
          phone_number: s.phone_number || "",
          address_line_1: s.address_line_1 || "",
          address_line_2: s.address_line_2 || "",
          city: s.city || "",
          state: s.state || "",
          country: s.country || "",
          zip_code: s.zip_code || "",
          is_default: !!sig.is_default,
          unsubscribe_message: sig.unsubscribe_message || "",
          signature_html: storedHtml,
          image_url: s.image_url || "",
          image_name: s.image_name || "",
        });
        // Source resolution priority:
        //   1. backend-persisted signature_mode (legacy records may have it)
        //   2. FE-only Gmail-mode marker present → Gmail
        //   3. signature_html present → Gmail
        //   4. structured fields present → Custom
        //   5. fallback Custom
        let resolvedSource;
        if (persistedMode) resolvedSource = persistedMode;
        else if (isGmailMarker) resolvedSource = SOURCE_GMAIL;
        else if (storedHtml) resolvedSource = SOURCE_GMAIL;
        else if (hasStructured) resolvedSource = SOURCE_CUSTOM;
        else resolvedSource = SOURCE_CUSTOM;
        setSource(resolvedSource);
        setShowUnsub(Boolean(sig.unsubscribe_message));
        setValidations({});
      } catch (err) {
        EventEmitter.emit(
          EMITTER_ERROR,
          getErrorMessage(err?.data?.data?.errors || {}, err?.data?.message),
        );
      } finally {
        setIsFetching(false);
      }
    })();
  }, [open, signatureId, isEdit, isFirstForMailbox, navigate]);

  // Sync placeholder visibility with the live editor contents - runs whenever
  // we (re)mount the editor (Gmail mode active + not fetching) or
  // signature_html is reset programmatically.
  useEffect(() => {
    if (source !== SOURCE_GMAIL || isFetching) return;
    const el = document.getElementById(HTML_EDITOR_ID);
    if (!el) return;
    const compute = () => {
      const html = el.innerHTML || "";
      const stripped = html.replace(/<br\s*\/?>(\s|&nbsp;)*/gi, "").trim();
      const isEmpty = stripped.length === 0;
      setEditorIsEmpty(isEmpty);
      if (!isEmpty) {
        setValidations((prev) => {
          if (!prev?.signature_body) return prev;
          const next = { ...prev };
          delete next.signature_body;
          return next;
        });
      }
    };
    compute();
    el.addEventListener("input", compute);
    el.addEventListener("blur", compute);
    return () => {
      el.removeEventListener("input", compute);
      el.removeEventListener("blur", compute);
    };
  }, [source, isFetching, draft.signature_html]);

  const updateField = (key) => (e) =>
    setDraft((prev) => ({ ...prev, [key]: e.target.value }));

  // For letters-only fields: silently strip digits + most punctuation as the
  // user types so they can't even submit "1234" as a first name.
  const updateAlphaField = (key) => (e) => {
    const cleaned = e.target.value.replace(/[^A-Za-zÀ-ɏ\s\-'.,&]/g, "");
    setDraft((prev) => ({ ...prev, [key]: cleaned }));
  };

  const validate = () => {
    const next = {};
    const name = draft.signature_name?.trim();
    if (!name) next.signature_name = "Signature name is required.";
    else if (name.length > 80)
      next.signature_name = "Signature name must be 80 characters or less.";

    const v = (key, fn) => {
      const r = fn();
      if (!r.isValid) next[key] = r.message;
    };
    // Sender first/last name must be letters-only (catches "1234" / digits).
    v("first_name", () =>
      validateAlphaField(draft.first_name, "Sender's first name"),
    );
    v("last_name", () =>
      validateAlphaField(draft.last_name, "Sender's last name"),
    );

    if (source === SOURCE_GMAIL) {
      // Gmail mode requires an actual signature body - either extracted via
      // /pull-gmail-signatures or typed/pasted manually. Without it we'd be
      // saving a "Gmail signature" that has no HTML to send.
      const htmlEl = document.getElementById(HTML_EDITOR_ID);
      const raw = htmlEl ? htmlEl.innerHTML.toString() : draft.signature_html;
      // Strip the FE-only Gmail-mode marker first so a legacy doc that only
      // contains the marker counts as empty.
      const withoutMarker = (raw || "").replace(
        /<div\s+data-sig-mode=["']gmail["']\s*>\s*<\/div>/gi,
        "",
      );
      // Use the live element's textContent if we have it (handles stripped
      // wrapper tags the regex below can miss); fall back to a DOMParser when
      // we only have the raw string. innerText excludes hidden / collapsed
      // whitespace, which is what we want.
      let visibleText = "";
      if (htmlEl) {
        visibleText = (htmlEl.textContent || "").replace(
          /[​-‍⁠﻿]/g,
          "",
        );
      } else if (typeof DOMParser !== "undefined") {
        try {
          const doc = new DOMParser().parseFromString(
            withoutMarker || "",
            "text/html",
          );
          visibleText = (doc.body?.textContent || "").replace(
            /[​-‍⁠﻿]/g,
            "",
          );
        } catch (e) {
          visibleText = "";
        }
      }
      const hasImage = /<img\b/i.test(withoutMarker);
      if (!visibleText.trim() && !hasImage) {
        next.signature_body =
          "Add a signature body - extract it or type one in.";
      }
    }

    if (source === SOURCE_CUSTOM) {
      // Job title & company can mix letters + numbers ("ACME 2.0", "Engineer III").
      v("job_title", () => campaignValidation("job", draft.job_title));
      v("company_name", () =>
        campaignValidation("company", draft.company_name),
      );
      v("address_line_1", () =>
        campaignValidation("address", draft.address_line_1),
      );
      // Place names should be letters-only.
      v("city", () => validateAlphaField(draft.city, "City"));
      v("state", () => validateAlphaField(draft.state, "State"));
      v("country", () => validateAlphaField(draft.country, "Country"));
      const zip = String(draft.zip_code ?? "").trim();
      if (!zip) next.zip_code = "Postal/Zip is required.";
      else if (zip.length < 3)
        next.zip_code = "Please enter a valid PIN code.";
      v("phone_number", () => validatePhoneNumber(draft.phone_number));
    }

    // Unsub section open hai toh content non-empty + tag mandatory.
    if (showUnsub) {
      const unsubEl = document.getElementById(UNSUB_EDITOR_ID);
      const html = unsubEl?.innerHTML?.toString() || "";
      const text = (unsubEl?.innerText || "").trim();
      const stripped = html
        .replace(/<br\s*\/?>/gi, "")
        .replace(/&nbsp;/gi, "")
        .replace(/<p[^>]*>\s*<\/p>/gi, "")
        .replace(/<div[^>]*>\s*<\/div>/gi, "")
        .replace(/\s+/g, "")
        .trim();
      const hasContent = !!text || !!stripped;
      if (!hasContent) {
        next.unsubscribe_message =
          "Add an unsubscribe message or close this section.";
      } else if (!text.includes("{{unsubscribe_tag}}")) {
        next.unsubscribe_message =
          "The {{unsubscribe_tag}} placeholder is required.";
      }
    }

    setValidations(next);
    return Object.keys(next).length === 0;
  };

  const pullFromGmail = async () => {
    if (!accountEmail) return;
    try {
      setIsPulling(true);
      const res = await postRequest(
        navigate,
        authEndpoints.pullSignatureRequest,
        { email: accountEmail },
      );
      const decoded = decodeStoredSignatureHtml(res?.data?.data);
      if (!decoded) {
        EventEmitter.emit(EMITTER_ERROR, STRINGS.EXTRACT_FROM_GMAIL_FAILED);
        return;
      }
      setDraft((prev) => ({ ...prev, signature_html: decoded }));
      try {
        const compressed = compressString(encodeURIComponent(decoded));
        await patchRequest(navigate, authEndpoints.saveSignatureRequest, {
          signature: compressed,
          email: accountEmail,
        });
      } catch (e) {
        /* non-fatal */
      }
      EventEmitter.emit(
        EMITTER_SUCCESS,
        res?.data?.message || "Signature fetched from mailbox",
      );
    } catch (err) {
      EventEmitter.emit(
        EMITTER_ERROR,
        getErrorMessage(
          err?.data?.data?.errors || {},
          err?.data?.message || STRINGS.EXTRACT_FROM_GMAIL_FAILED,
        ),
      );
    } finally {
      setIsPulling(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    // Reset the input so re-picking the same file fires onChange.
    e.target.value = "";
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      EventEmitter.emit(EMITTER_ERROR, STRINGS.SIG_IMAGE_INVALID);
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      EventEmitter.emit(EMITTER_ERROR, STRINGS.SIG_IMAGE_TOO_LARGE);
      return;
    }
    try {
      const base64 = await convertBase64(file);
      setDraft((prev) => ({
        ...prev,
        image_url: base64,
        image_name: file.name || "",
      }));
    } catch (err) {
      EventEmitter.emit(EMITTER_ERROR, STRINGS.SIG_IMAGE_INVALID);
    }
  };

  const handleImageRemove = () => {
    setDraft((prev) => ({ ...prev, image_url: "", image_name: "" }));
  };

  const handleSave = async () => {
    if (!source || source === "loading") return;
    if (!validate()) return;

    // For Gmail mode, read directly from the editor with no fallback. If the
    // user cleared the body, signatureHtml is "" and we MUST send that empty
    // string so the backend overwrites the stale stored HTML.
    let signatureHtml = "";
    let isGmailMode = source === SOURCE_GMAIL;
    if (isGmailMode) {
      const htmlEl = document.getElementById(HTML_EDITOR_ID);
      // contentEditable empty state can be "" or "<br>" - normalise both.
      const raw = htmlEl ? htmlEl.innerHTML.toString() : draft.signature_html;
      const trimmed = (raw || "").replace(/<br\s*\/?>(\s|&nbsp;)*/gi, "").trim();
      // Even when the editor is empty, send a tiny marker so the on-edit
      // inference treats this as a Gmail-mode signature (the backend doesn't
      // accept an explicit signature_mode field).
      signatureHtml = trimmed ? raw : GMAIL_MODE_MARKER;
    }

    let unsubMessage = "";
    if (showUnsub) {
      const unsubEl = document.getElementById(UNSUB_EDITOR_ID);
      const html = unsubEl?.innerHTML?.toString() || "";
      const text = unsubEl?.innerText || "";
      const stripped = html
        .replace(/<br\s*\/?>/gi, "")
        .replace(/&nbsp;/gi, "")
        .replace(/<p[^>]*>\s*<\/p>/gi, "")
        .replace(/<div[^>]*>\s*<\/div>/gi, "")
        .replace(/\s+/g, "")
        .trim();
      const isEmpty = !text.trim() && !stripped;
      if (!isEmpty && !text.includes("{{unsubscribe_tag}}")) {
        EventEmitter.emit(EMITTER_ERROR, "Unsubscribe tag is mandatory");
        return;
      }
      unsubMessage = isEmpty
        ? ""
        : removeTagStyleFromHtml(`<div>${html}</div>`);
    }

    const isCustom = source === SOURCE_CUSTOM;
    // Send only the fields the user actually filled in. Required identity
    // fields (name + sender first/last) always go in; everything else is
    // omitted when blank, including the optional unsubscribe + Gmail HTML.
    const payload = {
      signature_name: draft.signature_name.trim(),
      first_name: draft.first_name,
      last_name: draft.last_name,
    };
    if (isCustom) {
      const customFields = {
        job_title: draft.job_title,
        company_name: draft.company_name,
        phone_number: draft.phone_number?.trim() || "",
        address_line_1: draft.address_line_1,
        address_line_2: draft.address_line_2,
        city: draft.city,
        state: draft.state,
        country: draft.country,
        zip_code: draft.zip_code,
        image_url: draft.image_url,
        image_name: draft.image_name,
      };
      for (const [key, value] of Object.entries(customFields)) {
        if (value !== "" && value !== null && value !== undefined) {
          payload[key] = value;
        }
      }
    }
    // Always include unsubscribe_message in payload (even when empty) so the
    // backend doc is kept in sync - otherwise stale values can linger when the
    // user collapses/clears the unsub section.
    payload.unsubscribe_message = unsubMessage;
    // In Gmail mode, ALWAYS send signature_html (even an empty string). On
    // create the empty-string is harmless; on edit it's required so backend
    // can overwrite a previously-saved body the user just deleted.
    if (isGmailMode) {
      payload.signature_html = signatureHtml;
    }

    try {
      setIsSaving(true);
      let createdId = signatureId;
      if (isEdit) {
        const res = await patchRequest(
          navigate,
          `${CampaignSettingsEndpoints.signatureUpdate}/${signatureId}`,
          payload,
        );
        EventEmitter.emit(EMITTER_SUCCESS, res?.data?.message);
      } else {
        const res = await postRequest(
          navigate,
          CampaignSettingsEndpoints.signatureCreate,
          {
            ...payload,
            account_id: accountId,
            is_default: draft.is_default || isFirstForMailbox,
          },
        );
        createdId = res?.data?.data?._id || createdId;
        EventEmitter.emit(EMITTER_SUCCESS, res?.data?.message);
        window.dispatchEvent(new CustomEvent("setup:signature-auto-advance"));
      }
      if (isEdit && draft.is_default && createdId) {
        await patchRequest(
          navigate,
          `${CampaignSettingsEndpoints.signatureSetDefault}/${createdId}`,
        );
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      const status = err?.status;
      const message = err?.data?.message || "";
      const fieldErrors = err?.data?.data?.errors;
      if (fieldErrors && typeof fieldErrors === "object") {
        const boundToInput = {};
        const orphanMessages = [];
        for (const key of Object.keys(fieldErrors)) {
          const raw = fieldErrors[key];
          if (typeof raw !== "string" || !raw.length) continue;
          const friendly = FIELD_LABELS[key] || key;
          const message = raw.replace(/^"[^"]+"\s*/, `${friendly} `);
          if (FIELD_LABELS[key]) {
            // Errors on known form fields render inline next to the input.
            boundToInput[key] = message;
          } else {
            // Errors on fields the user can't see (e.g. unknown-field rejections
            // like "signature_mode is not allowed") need a toast or the user
            // never sees them.
            orphanMessages.push(message);
          }
        }
        if (Object.keys(boundToInput).length) {
          setValidations((prev) => ({ ...prev, ...boundToInput }));
        }
        if (orphanMessages.length) {
          EventEmitter.emit(EMITTER_ERROR, orphanMessages.join(" "));
        }
        if (Object.keys(boundToInput).length || orphanMessages.length) {
          return;
        }
      }
      const isDuplicate =
        status === 422 && /already exists/i.test(message);
      if (isDuplicate) {
        setValidations((prev) => ({ ...prev, signature_name: message }));
      } else {
        EventEmitter.emit(
          EMITTER_ERROR,
          getErrorMessage(fieldErrors || {}, message),
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const showDefaultToggle = !isFirstForMailbox;

  const modalHeaderData = {
    heading: {
      id: "signature-form",
      label: isEdit ? STRINGS.EDIT_SIG : STRINGS.NEW_SIGNATURE,
    },
    description: accountEmail
      ? { id: "signature-form-desc", label: accountEmail }
      : undefined,
  };

  // ─── Sub-renders ────────────────────────────────────────────────────

  const fieldLabel = (text) => (
    <TypographyInline
      size={"sm"}
      color={GREY.QUATINARY}
      fontWeight={fontWeight.MEDIUM}
      label={text}
    />
  );

  const renderSourcePicker = () => (
    <Box className={classes.sigSourcePicker}>
      <Box>
        <TypographyInline
          size={"md"}
          color={GREY.PRIMARY}
          fontWeight={fontWeight.MEDIUM}
          label={STRINGS.CHOOSE_SOURCE_TITLE}
        />
        <TypographyInline
          size={"sm"}
          color={GREY.SECONDARY}
          fontWeight={fontWeight.REGULAR}
          label={STRINGS.CHOOSE_SOURCE_HINT}
        />
      </Box>
      <Box className={classes.sigSourceCardGrid}>
        <button
          type="button"
          className={classes.sigSourceCard}
          onClick={() => setSource(SOURCE_GMAIL)}
        >
          <Box className={classes.sigSourceCardIcon}>
            <IconComponent
              iconLabel={ICON_LABELS.CLOUD_DOWNLOAD}
              fontSize={"22px"}
            />
          </Box>
          <TypographyInline
            size={"sm"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.MEDIUM}
            label={STRINGS.SOURCE_GMAIL_TITLE}
          />
          <TypographyInline
            size={"xs"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label={STRINGS.SOURCE_GMAIL_DESC}
          />
        </button>
        <button
          type="button"
          className={classes.sigSourceCard}
          onClick={() => setSource(SOURCE_CUSTOM)}
        >
          <Box className={classes.sigSourceCardIcon}>
            <IconComponent iconLabel={ICON_LABELS.EDIT} fontSize={"22px"} />
          </Box>
          <TypographyInline
            size={"sm"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.MEDIUM}
            label={STRINGS.SOURCE_CUSTOM_TITLE}
          />
          <TypographyInline
            size={"xs"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label={STRINGS.SOURCE_CUSTOM_DESC}
          />
        </button>
      </Box>
    </Box>
  );

  const renderSourceBar = () => {
    const label =
      source === SOURCE_GMAIL
        ? STRINGS.SOURCE_GMAIL_TITLE
        : STRINGS.SOURCE_CUSTOM_TITLE;
    const icon =
      source === SOURCE_GMAIL ? ICON_LABELS.CLOUD_DOWNLOAD : ICON_LABELS.EDIT;
    return (
      <Box className={classes.sigSourceBar}>
        <Box className={classes.sigSourceBarLeft}>
          <IconComponent
            iconLabel={icon}
            color={GREY.SECONDARY}
            fontSize={"18px"}
          />
          <TypographyInline
            size={"sm"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.MEDIUM}
            label={label}
          />
        </Box>
        {!isEdit && (
          <button
            type="button"
            className={classes.sigSourceBarChange}
            onClick={() => setSource(null)}
          >
            {STRINGS.CHANGE_SOURCE}
          </button>
        )}
      </Box>
    );
  };

  const renderIdentitySection = () => (
    <Box className={classes.sigFormSection}>
      <Box className={classes.sigFormField}>
        {fieldLabel(STRINGS.SIGNATURE_NAME)}
        <InputField
          size={"md40"}
          placeholder={STRINGS.ENTER_SIGNATURE_NAME}
          value={draft.signature_name}
          onChange={updateField("signature_name")}
          variant={
            validations?.signature_name ? STRINGS.ERROR : STRINGS.DEFAULT
          }
          errormessage={validations?.signature_name || ""}
        />
        {!validations?.signature_name && (
          <TypographyInline
            size={"xs"}
            color={GREY.TERTIARY}
            fontWeight={fontWeight.REGULAR}
            label={STRINGS.SIGNATURE_NAME_HELPER}
          />
        )}
      </Box>
      <Box className={classes.sigFormFieldGrid}>
        <Box className={classes.sigFormField}>
          {fieldLabel(STRINGS.SEND_FIRST_NAME)}
          <InputField
            size={"md40"}
            placeholder={STRINGS.ENTER_SEND_FIRST_NAME}
            value={draft.first_name}
            onChange={updateAlphaField("first_name")}
            variant={
              validations?.first_name ? STRINGS.ERROR : STRINGS.DEFAULT
            }
            errormessage={validations?.first_name || ""}
          />
        </Box>
        <Box className={classes.sigFormField}>
          {fieldLabel(STRINGS.SEND_LAST_NAME)}
          <InputField
            size={"md40"}
            placeholder={STRINGS.ENTER_SEND_LAST_NAME}
            value={draft.last_name}
            onChange={updateAlphaField("last_name")}
            variant={
              validations?.last_name ? STRINGS.ERROR : STRINGS.DEFAULT
            }
            errormessage={validations?.last_name || ""}
          />
        </Box>
      </Box>
    </Box>
  );

  const renderGmailBody = () => (
    <Box className={classes.sigFormSection}>
      {fieldLabel(STRINGS.EMAIL_SIGNATURE)}
      <Box
        className={classes.sigEditorWrap}
        style={
          validations?.signature_body
            ? { borderColor: PRIMARY.W_500 }
            : undefined
        }
      >
        {!isFetching && (
          <HtmlEditor
            id={HTML_EDITOR_ID}
            editorHeight="180px"
            isAITagNotVisible
            isAddTagNotVisible
            isMeetingTagNotVisible
            isFocusNotRequired
            previosData={draft.signature_html || ""}
          />
        )}
        {editorIsEmpty && !isFetching && (
          <span className={classes.sigEditorEmpty}>
            {STRINGS.EMPTY_SIGNATURE_PROMPT}
          </span>
        )}
        <Box className={classes.sigExtractAction}>
          <button
            type="button"
            className={classes.sigExtractBtn}
            onClick={pullFromGmail}
            disabled={!accountEmail || isPulling}
          >
            <IconComponent
              iconLabel={ICON_LABELS.CLOUD_DOWNLOAD}
              fontSize={"14px"}
            />
            {isPulling ? STRINGS.PULLING_FROM_GMAIL : STRINGS.EXTRACT_FROM_GMAIL}
          </button>
        </Box>
      </Box>
      {validations?.signature_body && (
        <TypographyInline
          size={"xs"}
          color={PRIMARY.W_500}
          fontWeight={fontWeight.REGULAR}
          label={validations.signature_body}
        />
      )}
    </Box>
  );

  const previewAddressLine = [
    draft.address_line_2,
    draft.city,
    draft.state,
    draft.country,
    draft.zip_code,
  ]
    .map((v) => (v ? String(v).replace(/[,\s]+$/, "").trim() : ""))
    .filter(Boolean)
    .join(", ");

  const previewHasContent = Boolean(
    draft.first_name ||
      draft.last_name ||
      draft.job_title ||
      draft.company_name ||
      draft.phone_number ||
      draft.address_line_1 ||
      previewAddressLine ||
      draft.image_url,
  );

  const renderCustomBody = () => (
    <Box className={classes.sigCustomBody}>
      <Box className={classes.sigCustomFields}>
        <Box className={classes.sigFormFieldGrid}>
          <Box className={classes.sigFormField}>
            {fieldLabel(STRINGS.JOB_TITLE)}
            <InputField
              size={"md40"}
              placeholder={STRINGS.ENTER_JOB_TITLE}
              value={draft.job_title}
              onChange={updateField("job_title")}
              variant={
                validations?.job_title ? STRINGS.ERROR : STRINGS.DEFAULT
              }
              errormessage={validations?.job_title || ""}
            />
          </Box>
          <Box className={classes.sigFormField}>
            {fieldLabel(STRINGS.CAMPANY_NAME)}
            <InputField
              size={"md40"}
              placeholder={STRINGS.ENTER_CAMPANY_NAME}
              value={draft.company_name}
              onChange={updateField("company_name")}
              variant={
                validations?.company_name ? STRINGS.ERROR : STRINGS.DEFAULT
              }
              errormessage={validations?.company_name || ""}
            />
          </Box>
        </Box>
        <Box className={classes.sigFormFieldGrid}>
          <Box className={classes.sigFormField}>
            {fieldLabel(STRINGS.PHONE_NUMBER)}
            <InputField
              size={"md40"}
              placeholder={STRINGS.ENTER_PHON_NUM}
              value={draft.phone_number}
              onChange={(e) => {
                // Allow digits, spaces, dashes, parens, dot and a leading +.
                const sanitized = e.target.value
                  .replace(/[^\d+\-().\s]/g, "")
                  .replace(/(?!^)\+/g, "");
                setDraft((prev) => ({ ...prev, phone_number: sanitized }));
              }}
              inputMode="tel"
              variant={
                validations?.phone_number ? STRINGS.ERROR : STRINGS.DEFAULT
              }
              errormessage={validations?.phone_number || ""}
            />
          </Box>
          <Box className={classes.sigFormField}>
            {fieldLabel(STRINGS.ADDRESS_LINE)}
            <InputField
              size={"md40"}
              placeholder={STRINGS.ENTER_ADD1}
              value={draft.address_line_1}
              onChange={updateField("address_line_1")}
              variant={
                validations?.address_line_1 ? STRINGS.ERROR : STRINGS.DEFAULT
              }
              errormessage={validations?.address_line_1 || ""}
            />
          </Box>
        </Box>
        <Box className={classes.sigFormField}>
          {fieldLabel(STRINGS.ADDRESS_LINE2)}
          <InputField
            size={"md40"}
            placeholder={STRINGS.ENTER_ADD2}
            value={draft.address_line_2}
            onChange={updateField("address_line_2")}
          />
        </Box>
        <Box className={classes.sigFormFieldGrid}>
          <Box className={classes.sigFormField}>
            {fieldLabel(STRINGS.CITY)}
            <InputField
              size={"md40"}
              placeholder={STRINGS.ENTER_CITY}
              value={draft.city}
              onChange={updateAlphaField("city")}
              variant={validations?.city ? STRINGS.ERROR : STRINGS.DEFAULT}
              errormessage={validations?.city || ""}
            />
          </Box>
          <Box className={classes.sigFormField}>
            {fieldLabel(STRINGS.STATE)}
            <InputField
              size={"md40"}
              placeholder={STRINGS.ENTER_STATE}
              value={draft.state}
              onChange={updateAlphaField("state")}
              variant={validations?.state ? STRINGS.ERROR : STRINGS.DEFAULT}
              errormessage={validations?.state || ""}
            />
          </Box>
        </Box>
        <Box className={classes.sigFormFieldGrid}>
          <Box className={classes.sigFormField}>
            {fieldLabel(STRINGS.COUNTRY)}
            <InputField
              size={"md40"}
              placeholder={STRINGS.ENTER_COUNTRY}
              value={draft.country}
              onChange={updateAlphaField("country")}
              variant={validations?.country ? STRINGS.ERROR : STRINGS.DEFAULT}
              errormessage={validations?.country || ""}
            />
          </Box>
          <Box className={classes.sigFormField}>
            {fieldLabel(STRINGS.POSTAL)}
            <InputField
              size={"md40"}
              placeholder={STRINGS.ENTER_POSTAL}
              value={draft.zip_code}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D+/g, "");
                setDraft((prev) => ({ ...prev, zip_code: digitsOnly }));
              }}
              inputMode="numeric"
              variant={validations?.zip_code ? STRINGS.ERROR : STRINGS.DEFAULT}
              errormessage={validations?.zip_code || ""}
            />
          </Box>
        </Box>

        <Box className={classes.sigFormField}>
          {fieldLabel(STRINGS.SIG_IMAGE_LABEL)}
          {draft.image_url ? (
            <Box className={classes.sigImageFilled}>
              <Box className={classes.sigImageThumbWrap}>
                <img
                  src={draft.image_url}
                  alt={draft.image_name || "Signature image"}
                  className={classes.sigImageThumb}
                />
                <Box className={classes.sigImageMeta}>
                  <TypographyInline
                    size={"sm"}
                    color={GREY.PRIMARY}
                    fontWeight={fontWeight.MEDIUM}
                    label={draft.image_name || "Signature image"}
                  />
                  <TypographyInline
                    size={"xs"}
                    color={GREY.TERTIARY}
                    fontWeight={fontWeight.REGULAR}
                    label={STRINGS.SIG_IMAGE_HINT}
                  />
                </Box>
              </Box>
              <Box className={classes.sigImageActions}>
                <label className={classes.sigImageGhostBtn}>
                  {STRINGS.SIG_IMAGE_REPLACE}
                  <input
                    type="file"
                    accept="image/*"
                    className={classes.sigImageHiddenInput}
                    onChange={handleImageUpload}
                  />
                </label>
                <button
                  type="button"
                  className={classes.sigImageGhostBtn}
                  onClick={handleImageRemove}
                >
                  {STRINGS.SIG_IMAGE_REMOVE}
                </button>
              </Box>
            </Box>
          ) : (
            <label className={classes.sigImageEmpty}>
              <input
                type="file"
                accept="image/*"
                className={classes.sigImageHiddenInput}
                onChange={handleImageUpload}
              />
              <span className={classes.sigImageUploadBtn}>
                <IconComponent
                  iconLabel={ICON_LABELS.CLOUD_DOWNLOAD}
                  fontSize={"14px"}
                />
                {STRINGS.SIG_IMAGE_UPLOAD}
              </span>
              <TypographyInline
                size={"xs"}
                color={GREY.TERTIARY}
                fontWeight={fontWeight.REGULAR}
                label={STRINGS.SIG_IMAGE_HINT}
              />
            </label>
          )}
        </Box>
      </Box>

      <Box className={classes.sigCustomPreview}>
        <Box className={classes.sigCustomPreviewHeader}>
          <TypographyInline
            size={"sm"}
            color={"#fff"}
            fontWeight={fontWeight.MEDIUM}
            label={STRINGS.SIGNATURE_PREVIEW}
          />
        </Box>
        <Box className={classes.sigCustomPreviewBody}>
          {previewHasContent ? (
            <>
              <TypographyInline
                size={"md"}
                color={GREY.PRIMARY}
                fontWeight={fontWeight.MEDIUM}
                label={`${draft.first_name || ""} ${draft.last_name || ""}`.trim()}
              />
              {draft.job_title && (
                <TypographyInline
                  size={"sm"}
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  label={draft.job_title}
                />
              )}
              {draft.image_url && (
                <img
                  src={draft.image_url}
                  alt={draft.image_name || "Signature image"}
                  className={classes.sigPreviewImage}
                />
              )}
              {draft.company_name && (
                <TypographyInline
                  size={"sm"}
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  label={draft.company_name}
                />
              )}
              {draft.phone_number && (
                <TypographyInline
                  size={"sm"}
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  label={draft.phone_number}
                />
              )}
              {draft.address_line_1 && (
                <p>
                  {`${String(draft.address_line_1).replace(/[,\s]+$/, "")},`}
                </p>
              )}
              {previewAddressLine && <p>{previewAddressLine}</p>}
            </>
          ) : (
            <span className={classes.sigCustomPreviewEmpty}>
              {STRINGS.SIGNATURE_PREVIEW_EMPTY}
            </span>
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderUnsubSection = () => (
    <Box className={classes.sigDisclosure}>
      <Box
        className={classes.sigDisclosureHeader}
        onClick={() => setShowUnsub((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setShowUnsub((v) => !v);
          }
        }}
      >
        <Box className={classes.sigDisclosureHeaderText}>
          <TypographyInline
            size={"sm"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.MEDIUM}
            label={STRINGS.UNSUSCRIBE}
          />
          <TypographyInline
            size={"xs"}
            color={GREY.TERTIARY}
            fontWeight={fontWeight.REGULAR}
            label={STRINGS.UNSUBSCRIBE_OPTIONAL_NOTE}
          />
        </Box>
        <IconComponent
          iconLabel={
            showUnsub ? ICON_LABELS.EXPAND_LESS : ICON_LABELS.EXPAND_MORE
          }
          color={GREY.SECONDARY}
          fontSize={"20px"}
        />
      </Box>
      {showUnsub && !isFetching && (
        <Box className={classes.sigDisclosureBody}>
          <Box className={classes.sigEditorWrap}>
            <HtmlEditor
              id={UNSUB_EDITOR_ID}
              editorHeight="120px"
              isAITagNotVisible
              isUnsubscirbeTagVisible
              isMeetingTagNotVisible
              isFocusNotRequired
              previosData={addTagStyleInHtml(
                draft.unsubscribe_message || DEFAULT_UNSUB,
              )}
            />
          </Box>
          {validations?.unsubscribe_message && (
            <TypographyInline
              size={"xs"}
              color={PRIMARY.W_500}
              fontWeight={fontWeight.REGULAR}
              label={validations.unsubscribe_message}
            />
          )}
        </Box>
      )}
    </Box>
  );

  // ─── Body composition ───────────────────────────────────────────────

  let modalBodyData;
  if (source === "loading") {
    modalBodyData = (
      <Box className={classes.sigForm}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "240px",
          }}
        >
          <CircularLoader size={40} />
        </Box>
      </Box>
    );
  } else if (!source) {
    modalBodyData = renderSourcePicker();
  } else {
    modalBodyData = (
      <Box className={classes.sigForm}>
        {renderSourceBar()}
        {renderIdentitySection()}
        {source === SOURCE_GMAIL ? renderGmailBody() : renderCustomBody()}
        {renderUnsubSection()}
        {showDefaultToggle && (
          <Box className={classes.sigDefaultRow}>
            <Checkbox
              checked={!!draft.is_default}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, is_default: e.target.checked }))
              }
            />
            <TypographyInline
              size={"sm"}
              color={GREY.PRIMARY}
              fontWeight={fontWeight.MEDIUM}
              label={STRINGS.SET_AS_DEFAULT}
            />
          </Box>
        )}
      </Box>
    );
  }

  const modalFooterRightData = (
    <Box sx={{ display: "flex", gap: "8px" }}>
      <Button
        label={STRINGS.CANCEL}
        size={"sm36"}
        variant={"ghost"}
        onClick={onClose}
      />
      {source && source !== "loading" && (
        <Button
          label={isEdit ? STRINGS.COMMON_UPDATE : STRINGS.COMMON_SAVE}
          size={"sm36"}
          variant={"primary"}
          onClick={handleSave}
          isLoading={isSaving}
        />
      )}
    </Box>
  );

  return (
    <CustomModal
      open={open}
      width={"880px"}
      close={onClose}
      header={modalHeaderData}
      children={modalBodyData}
      footer={{
        left: <div />,
        right: modalFooterRightData,
      }}
    />
  );
};

export default SignatureFormModal;
